import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OpenSeadragon from "openseadragon";
import "./StoryMode.css";

// Importation des Components
import SliderText from "../Slidertext/Slidertext.js";
import CustomButton from "../Buttons/CustomButton.js";
import TimeoutComponent from "../TimeoutComponent/TimeoutComponent";
import preventClick from "../../utils/PreventClick";

// Importation de composants personnalisés et d'icônes
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";


const StoryMode = () => {
	const [items, setItems] = useState([]);
	const [selectedItemIndex, setSelectedItemIndex] = useState(0);
	const [viewer, setViewer] = useState(null);
	const NAVIGATE = useNavigate();
	const LOCATION = useLocation();
	const [image_width, setImageWidth] = useState(0);

	// Fonction pour calculer le centre et le niveau de zoom pour les annotations
	const calculateCenterAndZoom = (X, Y, W, H, viewer) => {
		const centerX = (X + W / 2) / image_width;
		const centerY = (Y + H / 2) / image_width;
		const viewerWidth = viewer.container.clientWidth;
		const viewerHeight = viewer.container.clientHeight;
		const zoomWidth = viewerWidth / image_width / (W * 1.5 / image_width);
		const zoomHeight = viewerHeight / image_width / (H * 1.5 / image_width);
		const zoomFocal = 7;
		const calculatedZoom = Math.max(zoomWidth, zoomHeight) * zoomFocal;
		return { centerX, centerY, calculatedZoom };
	};

	TimeoutComponent(NAVIGATE);

	preventClick();

	// Hook useEffect pour récupérer les données et initialiser OpenSeadragon
	useEffect(() => {
		const fetchData = async () => {
			try {
				// Récupération des données du fichier JSON
				const responseJsonFile = await fetch(LOCATION.state);
				const dataJsonFile = await responseJsonFile.json();

				if (dataJsonFile?.source) {
					// Récupération des données IIIF
					const responseIIIF = await fetch(dataJsonFile.source);
					const dataIIIF = await responseIIIF.json();

					if (dataIIIF?.width) {
						setImageWidth(dataIIIF.width);
					}


					// Initialisation d'OpenSeadragon avec l'URL obtenue du JSON
					const OSD_VIEWER = OpenSeadragon({
						id: "openseadragon-viewer",
						tileSources: dataJsonFile.source,
						showZoomControl: false,
						showHomeControl: false,
						showFullPageControl: false,

					});

					setViewer(OSD_VIEWER);

					if (dataJsonFile?.first?.items) {
						setItems(dataJsonFile.first.items);
						addClickHandler(OSD_VIEWER, dataJsonFile.first.items);
					}
				}
			} catch (error) {
				console.error("Error fetching JSON:", error);
			}
		};

		fetchData();
	}, [LOCATION.state]);

	// Fonction pour ajouter un gestionnaire de clic sur le canvas OpenSeadragon
	const addClickHandler = (OSD_VIEWER, items) => {
		OSD_VIEWER.addHandler("canvas-click", (event) => {
			const CLICKED_ELEMENT = event.originalTarget;
			if (CLICKED_ELEMENT.classList.contains("white-annotation") || CLICKED_ELEMENT.classList.contains("svg-annotation")) {
				OSD_VIEWER.gestureSettingsMouse.clickToZoom = false;
				handleImageElementClick(CLICKED_ELEMENT.id, items, OSD_VIEWER);
			}
		});
	};

	// Fonction pour gérer les clics sur les éléments d'image (annotations)
	const handleImageElementClick = (index, items, OSD_VIEWER) => {
		const selectedItem = items[index];
		setSelectedItemIndex(index);
		const { value } = selectedItem.target.selector;
		const MATCH = value.match(/xywh=pixel:([0-9.]+),([0-9.]+),([0-9.]+),([0-9.]+)/);
		if (MATCH) {
			const [X, Y, W, H] = MATCH.slice(1).map(Number);
			const { centerX, centerY, calculatedZoom } = calculateCenterAndZoom(X, Y, W, H, OSD_VIEWER);
			OSD_VIEWER.viewport.panTo(new OpenSeadragon.Point(centerX, centerY));
			OSD_VIEWER.viewport.zoomTo(calculatedZoom);
		}
	};

	const useDisablePinchZoomEffect = () => {
		useEffect(() => {
			const disablePinchZoom = (e) => {
				if (e.touches.length > 1) {
					e.preventDefault();
				}
			};
			document.addEventListener("touchmove", disablePinchZoom, { passive: false });
			return () => {
				document.removeEventListener("touchmove", disablePinchZoom);
			};
		}, []);
	};

	// Hook useEffect pour le zoom et le panoramique vers l'annotation sélectionnée
	useEffect(() => {
		if (viewer && items.length > 0) {
			const selectedItem = items[selectedItemIndex];
			const { value } = selectedItem.target.selector;
			const MATCH = value.match(/xywh=pixel:([0-9.]+),([0-9.]+),([0-9.]+),([0-9.]+)/);
			if (MATCH) {
				const [X, Y, W, H] = MATCH.slice(1).map(Number);
				const { centerX, centerY, calculatedZoom } = calculateCenterAndZoom(X, Y, W, H, viewer);
				viewer.viewport.panTo(new OpenSeadragon.Point(centerX, centerY));
				viewer.viewport.zoomTo(calculatedZoom);
			}
		}
	}, [viewer, items, selectedItemIndex]);

	// Fonction pour gérer le clic sur le bouton de navigation gauche
	const handleLeftButtonClick = () => {
		if (selectedItemIndex > 0) {
			handleImageElementClick(selectedItemIndex - 1, items, viewer);
		}
	};

	// Fonction pour gérer le clic sur le bouton de navigation droite
	const handleRightButtonClick = () => {
		if (selectedItemIndex < items.length - 1) {
			handleImageElementClick(selectedItemIndex + 1, items, viewer);

		}
	};

	return (
		<div>
			<div className="container-story">
				<div className="left-part-story">
					<div className="annotation-container-story" style={{ flex: "1" }}>


						<SliderText className="slider-text"
							htmlContent={
								items[selectedItemIndex]?.body.find(part => part.type === "HTMLBody")?.value
							}
						/>

					</div>
					<div className="navigation-container-story">
						<CustomButton
							isDisabled={false}
							actionToDo={handleLeftButtonClick}
							icon={<ArrowBackIcon style={{ fontSize: 40 }} />}
						/>


						<p className="progress-story">{selectedItemIndex + 1} / {items.length}</p>

						<CustomButton
							isDisabled={false}
							actionToDo={handleRightButtonClick}
							icon={<ArrowForwardIcon style={{ fontSize: 40 }} />}
						/>



					</div>

					<div className={"home-section-story"}>
						<CustomButton
							isDisabled={false}
							icon={<HomeOutlinedIcon style={{ fontSize: 40 }} />}
							actionToDo={() => { NAVIGATE("/"); }}
						/>
					</div>
				</div>

				<div className="right-part-story">


					<div
						id="openseadragon-viewer"
						style={{

							width: "100%",
							height: "100vh",
						}}
					>
						{/* This div will be used by OpenSeadragon to render the viewer */}
					</div>

					<div className="floating-button-story">

						<CustomButton
							isDisabled={false}
							actionToDo={() => handleImageElementClick(selectedItemIndex, items, viewer)}
							icon={<CenterFocusWeakIcon style={{ fontSize: 40 }} />}
						/>

					</div>


				</div>
			</div>

		</div>

	);
};

export default StoryMode;